import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { API, useAuth } from "@/App";
import UserLayout from "@/components/layouts/UserLayout";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Wallet,
  TrendingUp,
  Users,
  Clock,
  ArrowUpRight,
  ExternalLink,
} from "lucide-react";
import { toast } from "sonner";
import ReferralShareCard from "@/components/ReferralShareCard";

const UserDashboard = () => {
  const { user, getAuthHeaders } = useAuth();
  const [dashboard, setDashboard] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchDashboard();
  }, []);

  const fetchDashboard = async () => {
    try {
      const res = await axios.get(`${API}/users/dashboard`, {
        headers: getAuthHeaders(),
      });
      setDashboard(res.data);
    } catch (error) {
      toast.error("Failed to load dashboard");
    }
    setLoading(false);
  };

  if (loading) {
    return (
      <UserLayout title="Dashboard">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
          {[...Array(4)].map((_, i) => (
            <Card key={i} className="animate-pulse">
              <CardContent className="p-6">
                <div className="h-4 bg-gray-200 rounded w-1/2 mb-2" />
                <div className="h-8 bg-gray-200 rounded w-3/4" />
              </CardContent>
            </Card>
          ))}
        </div>
      </UserLayout>
    );
  }

  const wallet = dashboard?.wallet || { pending_balance: 0, approved_balance: 0, withdrawn_balance: 0 };
  const totalEarnings = dashboard?.total_earnings || 0;

  return (
    <UserLayout title="Dashboard">
      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-8 stagger-children">
        <Card className="border-0 shadow-sm">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500 mb-1">Total Earnings</p>
                <p className="text-2xl font-bold text-gray-900 font-heading">
                  ₹{totalEarnings.toLocaleString()}
                </p>
              </div>
              <div className="w-12 h-12 bg-primary-50 rounded-xl flex items-center justify-center">
                <TrendingUp className="w-6 h-6 text-primary-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="border-0 shadow-sm">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500 mb-1">Pending</p>
                <p className="text-2xl font-bold text-yellow-600 font-heading">
                  ₹{wallet.pending_balance.toLocaleString()}
                </p>
              </div>
              <div className="w-12 h-12 bg-yellow-50 rounded-xl flex items-center justify-center">
                <Clock className="w-6 h-6 text-yellow-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="border-0 shadow-sm">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500 mb-1">Approved</p>
                <p className="text-2xl font-bold text-green-600 font-heading">
                  ₹{wallet.approved_balance.toLocaleString()}
                </p>
              </div>
              <div className="w-12 h-12 bg-green-50 rounded-xl flex items-center justify-center">
                <Wallet className="w-6 h-6 text-green-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="border-0 shadow-sm">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500 mb-1">Referrals</p>
                <p className="text-2xl font-bold text-blue-600 font-heading">
                  {dashboard?.referral_count || 0}
                </p>
              </div>
              <div className="w-12 h-12 bg-blue-50 rounded-xl flex items-center justify-center">
                <Users className="w-6 h-6 text-blue-600" />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      <div className="grid lg:grid-cols-3 gap-6">
        {/* Referral Share Card */}
        <div className="lg:col-span-2">
          <ReferralShareCard
            referralCode={user?.referral_code}
            referralLink={`${window.location.origin}/register?ref=${user?.referral_code}`}
            compact
          />
        </div>

        {/* Quick Actions */}
        <Card className="border-0 shadow-sm">
          <CardHeader>
            <CardTitle>Quick Actions</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <Link to="/wallet">
              <Button variant="outline" className="w-full justify-between">
                View Wallet
                <ArrowUpRight className="w-4 h-4" />
              </Button>
            </Link>
            <Link to="/referrals">
              <Button variant="outline" className="w-full justify-between">
                View Referral Tree
                <ArrowUpRight className="w-4 h-4" />
              </Button>
            </Link>
            <Link to="/products">
              <Button className="w-full bg-primary-500 hover:bg-primary-600 justify-between">
                Browse Deals
                <ExternalLink className="w-4 h-4" />
              </Button>
            </Link>
          </CardContent>
        </Card>
      </div>

      {/* Recent Clicks */}
      <Card className="mt-6 border-0 shadow-sm">
        <CardHeader>
          <CardTitle>Recent Activity</CardTitle>
        </CardHeader>
        <CardContent>
          {dashboard?.recent_clicks?.length > 0 ? (
            <div className="space-y-3">
              {dashboard.recent_clicks.map((click, idx) => (
                <div
                  key={click.id || idx}
                  className="flex items-center justify-between p-3 bg-gray-50 rounded-xl"
                >
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-primary-100 rounded-lg flex items-center justify-center">
                      <ExternalLink className="w-5 h-5 text-primary-600" />
                    </div>
                    <div>
                      <p className="font-medium text-gray-900">Product Click</p>
                      <p className="text-sm text-gray-500">
                        {new Date(click.clicked_at).toLocaleDateString()}
                      </p>
                    </div>
                  </div>
                  <Badge className="bg-green-100 text-green-700">Tracked</Badge>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-8">
              <Clock className="w-12 h-12 text-gray-300 mx-auto mb-3" />
              <p className="text-gray-500">No recent activity</p>
              <Link to="/products">
                <Button variant="link" className="text-primary-600 mt-2">
                  Start shopping to earn cashback
                </Button>
              </Link>
            </div>
          )}
        </CardContent>
      </Card>
    </UserLayout>
  );
};

export default UserDashboard;
